import React, { useEffect, useState, useRef, memo } from 'react';
import { UploadIcon, PlusIcon, XIcon, InfoIcon, CheckIcon, ImageIcon } from 'lucide-react';
export const PreviewManager = ({
  onAddMultipleVideos
}) => {
  const [title, setTitle] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  // Initialize channel name from localStorage or use default value
  const [channelName, setChannelName] = useState(() => {
    const savedChannelName = localStorage.getItem('channelName');
    return savedChannelName || 'Your Channel';
  });
  // Initialize podcast cover image from localStorage
  const [podcastCoverImage, setPodcastCoverImage] = useState(() => {
    const savedCoverImage = localStorage.getItem('podcastCoverImage');
    return savedCoverImage || null;
  });
  const fileInputRef = useRef(null);
  const coverImageInputRef = useRef(null);
  const dropAreaRef = useRef(null);
  // Save channel name to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('channelName', channelName);
  }, [channelName]);
  // Save podcast cover image to localStorage whenever it changes
  useEffect(() => {
    if (podcastCoverImage) {
      localStorage.setItem('podcastCoverImage', podcastCoverImage);
    } else {
      localStorage.removeItem('podcastCoverImage');
    }
  }, [podcastCoverImage]);
  // Convert file to base64
  const fileToBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };
  const handleFileUpload = async e => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(Array.from(files));
    }
  };
  const handleCoverImageUpload = async e => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        try {
          const base64Data = await fileToBase64(file);
          setPodcastCoverImage(base64Data);
        } catch (error) {
          console.error('Error converting cover image to base64:', error);
        }
      }
    }
  };
  const processFiles = async files => {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    // Process each file and convert to base64
    const processedImages = await Promise.all(imageFiles.map(async file => {
      try {
        const base64Data = await fileToBase64(file);
        return {
          id: Date.now() + '-' + Math.random().toString(36).substring(2, 9),
          file,
          url: base64Data,
          title: file.name.replace(/\.[^/.]+$/, '') // Remove file extension
        };
      } catch (error) {
        console.error('Error converting file to base64:', error);
        return null;
      }
    }));
    // Filter out any nulls from failed conversions
    const validImages = processedImages.filter(img => img !== null);
    setUploadedImages([...uploadedImages, ...validImages]);
  };
  const removeImage = index => {
    const newImages = [...uploadedImages];
    // No need to revoke URLs since we're using base64
    newImages.splice(index, 1);
    setUploadedImages(newImages);
  };
  const updateImageTitle = (index, newTitle) => {
    const newImages = [...uploadedImages];
    newImages[index].title = newTitle;
    setUploadedImages(newImages);
  };
  const handleDragOver = e => {
    e.preventDefault();
    if (dropAreaRef.current) {
      dropAreaRef.current.classList.add('bg-[#1e1e1e]');
    }
  };
  const handleDragLeave = () => {
    if (dropAreaRef.current) {
      dropAreaRef.current.classList.remove('bg-[#1e1e1e]');
    }
  };
  const handleDrop = e => {
    e.preventDefault();
    if (dropAreaRef.current) {
      dropAreaRef.current.classList.remove('bg-[#1e1e1e]');
    }
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(Array.from(e.dataTransfer.files));
    }
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (uploadedImages.length === 0) {
      alert('Please upload at least one image');
      return;
    }
    // Create a custom thumbnail preview for each image
    const thumbnails = uploadedImages.map(image => {
      // If a form title is provided, use it for all thumbnails
      // Otherwise, use the individual image title or fall back to default
      const thumbnailTitle = title ? title : image.title || 'Untitled Thumbnail';
      const thumbnailObject = {
        id: image.id,
        title: thumbnailTitle,
        channelName,
        views: Math.floor(Math.random() * 100) + ' views',
        timestamp: 'Just now',
        duration: '10:00',
        customThumbnail: image.url,
        podcastCoverImage: podcastCoverImage,
        videoId: null,
        isCustom: true // Mark as custom
      };
      console.log('Creating thumbnail object:', {
        id: thumbnailObject.id,
        title: thumbnailObject.title,
        videoId: thumbnailObject.videoId,
        hasCustomThumbnail: Boolean(thumbnailObject.customThumbnail),
        hasPodcastCoverImage: Boolean(thumbnailObject.podcastCoverImage),
        customThumbnailType: thumbnailObject.customThumbnail ? thumbnailObject.customThumbnail.startsWith('data:') ? 'base64' : 'objectURL' : 'none',
        customThumbnailLength: thumbnailObject.customThumbnail ? thumbnailObject.customThumbnail.substring(0, 30) + '...' : 'none'
      });
      return thumbnailObject;
    });
    console.log(`Adding ${thumbnails.length} thumbnails as custom videos`);
    onAddMultipleVideos(thumbnails);
    // Reset form
    setTitle('');
    setUploadedImages([]);
    setIsExpanded(false);
  };
  return <div className="bg-[#1a1a1a] rounded-xl p-4 mb-6 border border-[#303030]" data-id="element-167">
      <div className="flex items-center justify-between mb-4" data-id="element-168">
        <div className="flex items-center" data-id="element-169">
          <h2 className="text-xl font-medium" data-id="element-170">
            Podcast Title and Thumbnail Previewer
          </h2>
          <div className="ml-2 text-gray-400 cursor-pointer group relative" data-id="element-171">
            <InfoIcon size={16} data-id="element-172" />
            <div className="absolute left-0 bottom-full mb-2 w-64 bg-[#282828] p-2 rounded text-xs invisible group-hover:visible shadow-lg z-10" data-id="element-173">
              Upload custom thumbnails to see how they'll appear in YouTube,
              Spotify, and Apple Podcasts.
            </div>
          </div>
        </div>
        <button onClick={() => setIsExpanded(!isExpanded)} className="flex items-center text-blue-400 hover:text-blue-300 bg-[#263850] hover:bg-[#32465c] px-3 py-1.5 rounded-full text-sm" data-id="element-174">
          {isExpanded ? <>
              <XIcon size={16} className="mr-1" data-id="element-175" />
              Close
            </> : <>
              <PlusIcon size={16} className="mr-1" data-id="element-176" />
              Add thumbnails
            </>}
        </button>
      </div>
      {isExpanded && <form onSubmit={handleSubmit} className="space-y-3" data-id="element-177">
          <div data-id="element-178">
            <label htmlFor="title" className="block mb-1 font-medium text-sm" data-id="element-179">
              Title
            </label>
            <input id="title" type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Default title (optional)" className="w-full bg-[#121212] border border-[#303030] rounded-lg py-2 px-3 focus:outline-none focus:border-blue-500 text-sm" maxLength={100} data-id="element-180" />
            <div className="text-right text-xs text-gray-400 mt-1" data-id="element-181">
              {title.length}/100
            </div>
          </div>
          <div data-id="element-182">
            <div className="flex justify-between items-center mb-2" data-id="element-183">
              <label className="block font-medium text-sm" data-id="element-184">
                Thumbnail Images ({uploadedImages.length})
              </label>
              {uploadedImages.length > 0 && <button type="button" onClick={() => {
            // No need to revoke URLs since we're using base64
            setUploadedImages([]);
          }} className="text-xs text-red-400 hover:text-red-300" data-id="element-185">
                  Clear All
                </button>}
            </div>
            <div ref={dropAreaRef} className="bg-[#121212] border-2 border-[#303030] border-dashed rounded-lg flex flex-col items-center justify-center h-40 transition-colors duration-200" onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} data-id="element-186">
              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileUpload} className="hidden" data-id="element-187" />
              <UploadIcon size={32} className="text-gray-400 mb-2" data-id="element-188" />
              <p className="text-gray-300 text-sm mb-2" data-id="element-189">
                Drag and drop images here
              </p>
              <button type="button" onClick={() => fileInputRef.current?.click()} className="bg-[#272727] hover:bg-[#3f3f3f] text-white py-1.5 px-3 rounded-lg text-sm" data-id="element-190">
                Browse Files
              </button>
              <p className="text-gray-500 text-xs mt-2" data-id="element-191">
                Supports: JPG, PNG, GIF, WEBP
              </p>
            </div>
            {uploadedImages.length > 0 && <div className="mt-4" data-id="element-192">
                <h3 className="text-sm font-medium mb-2" data-id="element-193">Uploaded Images</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-80 overflow-y-auto p-1" data-id="element-194">
                  {uploadedImages.map((image, index) => <div key={index} className="relative group rounded-lg overflow-hidden border border-[#303030] bg-[#121212]" data-id="element-195">
                      <div className="aspect-video" data-id="element-196">
                        <img src={image.url} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" data-id="element-197" />
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200" data-id="element-198"></div>
                      <button type="button" onClick={() => removeImage(index)} className="absolute top-1 right-1 bg-black bg-opacity-70 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-199">
                        <XIcon size={14} data-id="element-200" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 p-2" data-id="element-201">
                        <input type="text" value={image.title} onChange={e => updateImageTitle(index, e.target.value)} className="w-full bg-transparent border-b border-gray-500 text-white text-xs py-1 px-0 focus:outline-none focus:border-blue-500" placeholder="Enter title" data-id="element-202" />
                      </div>
                    </div>)}
                </div>
              </div>}
          </div>
          <div className="mt-2" data-id="element-203">
            <label htmlFor="channelName" className="block mb-1 font-medium text-sm" data-id="element-204">
              Channel Name
            </label>
            <input id="channelName" type="text" value={channelName} onChange={e => setChannelName(e.target.value)} placeholder="Your channel name" className="w-full bg-[#121212] border border-[#303030] rounded-lg py-2 px-3 focus:outline-none focus:border-blue-500 text-sm" maxLength={50} data-id="element-205" />
          </div>
          {/* Podcast Cover Image Section */}
          <div className="mt-2" data-id="element-206">
            <label htmlFor="podcastCoverImage" className="block mb-1 font-medium text-sm" data-id="element-207">
              Podcast Cover Image
            </label>
            <div className="flex items-start space-x-3" data-id="element-208">
              <div className="flex-shrink-0 w-24 h-24 bg-[#121212] border border-[#303030] rounded-lg overflow-hidden" data-id="element-209">
                {podcastCoverImage ? <img src={podcastCoverImage} alt="Podcast Cover" className="w-full h-full object-cover" data-id="element-210" /> : <div className="w-full h-full flex items-center justify-center text-gray-500" data-id="element-211">
                    <ImageIcon size={24} data-id="element-212" />
                  </div>}
              </div>
              <div className="flex-1" data-id="element-213">
                <p className="text-xs text-gray-400 mb-2" data-id="element-214">
                  Upload a square image (1400x1400px recommended) to use as your
                  podcast cover in Apple Podcasts.
                </p>
                <div className="flex space-x-2" data-id="element-215">
                  <input ref={coverImageInputRef} type="file" accept="image/*" onChange={handleCoverImageUpload} className="hidden" data-id="element-216" />
                  <button type="button" onClick={() => coverImageInputRef.current?.click()} className="bg-[#272727] hover:bg-[#3f3f3f] text-white py-1.5 px-3 rounded-lg text-sm" data-id="element-217">
                    {podcastCoverImage ? 'Change Cover' : 'Upload Cover'}
                  </button>
                  {podcastCoverImage && <button type="button" onClick={() => setPodcastCoverImage(null)} className="bg-[#272727] hover:bg-[#3f3f3f] text-red-400 py-1.5 px-3 rounded-lg text-sm" data-id="element-218">
                      Remove
                    </button>}
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end pt-2" data-id="element-219">
            <button type="button" onClick={() => setIsExpanded(false)} className="mr-2 bg-[#272727] hover:bg-[#3f3f3f] text-white py-2 px-4 rounded-lg text-sm" data-id="element-220">
              Cancel
            </button>
            <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg flex items-center text-sm" disabled={uploadedImages.length === 0} data-id="element-221">
              <CheckIcon size={16} className="mr-2" data-id="element-222" />
              Add {uploadedImages.length}{' '}
              {uploadedImages.length === 1 ? 'Thumbnail' : 'Thumbnails'}
            </button>
          </div>
        </form>}
      {!isExpanded && <p className="text-gray-400 text-sm" data-id="element-223">
          Upload title and thumbnail ideas to see how they'll appear in YouTube,
          Spotify, and Apple Podcasts.
        </p>}
    </div>;
};